// Copyright (c) Docugami, Inc. All rights reserved.

import { ExternalClickType, useExternalClickHandler } from 'hooks/ExternalClickHandler';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { ClauseCardlet } from './ClauseCardlet';
import { AaScroller } from './ClauseSuggestionsList';

export interface IClause {
    id: string;
    dgml: string;
    documentId: string;
}

export interface IClauseItem {
    id: string;
    label: string;
    variants: IClause[];
}

const ListContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-bottom: 20px;
    margin: 0px 5px;
`;

const ScrollerContainer = styled(AaScroller)`
    width: 100%;
`;

export interface ClauseListProps {
    documentId: string;
    items: IClauseItem[];
}

export const ClauseList = (props: ClauseListProps): JSX.Element => {
    const [selectedClause, setSelectedClause] = useState('');
    const [selectedVariantId, setSelectedVariantId] = useState('');

    const handleClickOutside = useCallback(() => { setSelectedClause(''); setSelectedVariantId(''); }, [])
    const listRef = useExternalClickHandler<HTMLDivElement>(ExternalClickType.Children, handleClickOutside);

    function onSelect(id: string, variantId?: string, fallbackVariantId?: string) {
        if (selectedClause === id && !!variantId && variantId !== selectedVariantId) {
            // Selecting a different, valid variant
            setSelectedVariantId(variantId);
        } else {
            setSelectedClause(id);
            setSelectedVariantId(variantId ?? fallbackVariantId ?? '');
        }
    }

    return (
        <ScrollerContainer>
            <ListContainer ref={listRef}>
                {
                    props.items.map(item => {
                        if (!item) {
                            return null;
                        }
                
                        return (
                            <ClauseCardlet
                                key={item.id}
                                documentId={props.documentId}
                                clauseItem={item}
                                isSelected={item.id === selectedClause}
                                onSelect={(variantId) => onSelect(item.id, variantId, item.variants[0]?.id)}
                                selectedVariantId={selectedVariantId} />
                        )
                    })
                }
            </ListContainer>
        </ScrollerContainer>
    )
}
